import { httpClient } from '@/lib/api';
import { setCompanyName } from '@/lib/company-name';

/**
 * Identité de l'entreprise qui exploite l'instance.
 *
 * Miroir de `IssuerIdentityDto`
 * (`Backend/src/modules/settings/issuer.service.ts`).
 *
 * ── Deux versions, et ce n'est pas un oubli ──────────────────────────────
 * `version` verrouille les champs de l'identité, `logoVersion` le logo. Les
 * deux s'incrémentent séparément : sans cela, un changement d'adresse
 * invaliderait un téléversement de logo en cours dans un autre onglet, et
 * réciproquement.
 */
export interface IssuerIdentity {
  name: string;
  legalForm: string | null;
  /** Chaîne, comme tous les décimaux de l'API : un flottant fausserait un cumul. */
  shareCapital: string | null;
  rcsNumber: string | null;
  rcsCity: string | null;
  siret: string | null;
  vatNumber: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  email: string | null;
  phone: string | null;
  websiteUrl: string | null;
  logoUrl: string | null;
  logoVersion: number;
  /** `0` signifie « rien en base » : l'enregistrement crée au lieu de modifier. */
  version: number;
  updatedAt: string | null;
  /**
   * `true` quand les mentions indispensables à une facture sont réunies.
   *
   * Calculé par le serveur : c'est la même règle qui décidera si un document
   * peut être émis, et deux implémentations divergeraient.
   */
  isComplete: boolean;
}

/** Ce que le formulaire envoie. Le logo passe par ses propres routes. */
export type IssuerIdentityInput = Omit<
  IssuerIdentity,
  'logoUrl' | 'logoVersion' | 'updatedAt' | 'isComplete'
>;

export const issuerKey = ['issuer-identity'] as const;

export const issuerApi = {
  /* Le nom est publié au passage, comme `settingsApi` publie la devise : les
     écrans qui désignent la partie interne le lisent sans le recevoir en
     propriété. */
  read: async (): Promise<IssuerIdentity> => {
    const identity = await httpClient.get<IssuerIdentity>('/settings/issuer');
    setCompanyName(identity.name);
    return identity;
  },

  save: async (input: IssuerIdentityInput): Promise<IssuerIdentity> => {
    const identity = await httpClient.put<IssuerIdentity>('/settings/issuer', input);
    setCompanyName(identity.name);
    return identity;
  },

  /**
   * `POST /settings/issuer/logo` — multipart.
   *
   * La version voyage en champ de formulaire, donc en chaîne : c'est le serveur
   * qui la reconvertit. Délai allongé : un logo se téléverse, et le défaut de
   * vingt secondes coupe sur une connexion lente.
   */
  saveLogo: (file: File, logoVersion: number): Promise<IssuerIdentity> => {
    const body = new FormData();
    body.append('image', file, file.name);
    body.append('logoVersion', String(logoVersion));
    return httpClient.post<IssuerIdentity>('/settings/issuer/logo', body, { timeoutMs: 60_000 });
  },

  removeLogo: (logoVersion: number): Promise<IssuerIdentity> =>
    httpClient.delete<IssuerIdentity>('/settings/issuer/logo', { body: { logoVersion } }),
};
